import React, { useCallback, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import { getProviderUsersAction, updateProviderUsersAction } from '@/domain/paySettings/actions';

import useSearchAndSort from '@/pages/configurations/hooks/useSearchAndSort';
import useDispatchAction from '@/hooks/useDispatchAction';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import SearchBaseRaw from '@/components/mui/Layouts/components/AppbarSearchBase';
const SearchBase = SearchBaseRaw as any;
import NameCell from '@/pages/configurations/components/Table/components/NameCell';
import TableRaw from '@/pages/configurations/components/Table';
const Table = TableRaw as any;
import ToggleCell from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/UsersList/components/ToggleCell';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';

import { PROVIDER_USER_GROUPS } from '@/domain/paySettings/helpers';

const sortUsers = (users: any[]) => users.sort((a, b) => a.username.localeCompare(b.username));

interface UsersListProps {
  providerId: string;
}

const UsersList: React.FC<UsersListProps> = ({ providerId }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [selected, setSelected] = useState<string[]>([]);
  const [updatingUsers, setUpdatingUsers] = useState(false);

  const payload = useMemo(() => ({ id: providerId }), [providerId]);
  const { data: users, isLoading, setData: updateUsers } = useDispatchAction(getProviderUsersAction, payload, []);

  const generateStringForSearch = useCallback((user: any) => `${user.fullName} ${user.username}`, []);

  const { debounceOnChangeSearch, handleChangeSort, searchText, rows, sort, sortBy } = useSearchAndSort({
    generateStringForSearch,
    initialList: users,
    defaultSortFn: sortUsers,
  });

  const handleUpdateUsers = useCallback(
    ({ username, value, groupName }: { username: string; value: boolean; groupName: string }) => {
      setUpdatingUsers(true);
      new Promise((resolve, reject) => {
        dispatch(
          updateProviderUsersAction({
            payload: { usernames: selected.length ? selected : [username], value, groupName, id: providerId },
            resolve,
            reject,
          }),
        );
      })
        .then((providerUsers) => {
          updateUsers(providerUsers);
        })
        .finally(() => setUpdatingUsers(false));
    },
    [dispatch, selected, updateUsers, providerId, setUpdatingUsers],
  );

  const columns = useMemo(
    () => [
      {
        id: 'fullName',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.userRoles.tableHeader.user',
          defaultMessage: 'Full name',
        }),
        styles: { minWidth: 170 },
        component: (user: any) => <NameCell name={user.fullName} picture={user.avatar} />,
      },
      {
        id: 'username',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.userRoles.tableHeader.email',
          defaultMessage: 'Email',
        }),
        styles: { minWidth: 100 },
      },
      {
        id: 'isController',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.userRoles.tableHeader.controller',
          defaultMessage: 'Controller',
        }),
        styles: { minWidth: 100 },
        component: (user: any) => (
          <ToggleCell
            value={user.isController}
            username={user.username}
            groupName={PROVIDER_USER_GROUPS.controller}
            updateUsers={handleUpdateUsers}
          />
        ),
      },
      {
        id: 'isApprover',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.userRoles.tableHeader.approver',
          defaultMessage: 'Approver',
        }),
        styles: { minWidth: 100 },
        component: (user: any) => (
          <ToggleCell
            value={user.isApprover}
            username={user.username}
            groupName={PROVIDER_USER_GROUPS.approver}
            updateUsers={handleUpdateUsers}
          />
        ),
      },
    ],
    [formatMessage, handleUpdateUsers],
  );

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = (rows as any).toJS().map(({ username }: any) => username);
        setSelected(newSelected);
        return;
      }
      setSelected([]);
    },
    [rows],
  );

  return (
    <Stack sx={{ flex: '1 1 0', overflowY: 'hidden' }}>
      <CircularProgressWithBackdrop isOpen={updatingUsers} />
      <Box p={2}>
        <SearchBase value={searchText} onChange={debounceOnChangeSearch} />
      </Box>
      {isLoading ? (
        <LinearProgress />
      ) : (
        <Table
          data={rows}
          selected={selected}
          sort={sort}
          sortBy={sortBy}
          columns={columns}
          handleChangeSort={handleChangeSort}
          handleSelectAllClick={handleSelectAllClick}
          setSelected={setSelected}
          withCheckbox
        />
      )}
    </Stack>
  );
};

export default UsersList;
